import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import zeiterfassungService from '../services/zeiterfassungService';
import { StempelResponse, StempelStatus } from '../types';
import { Icon, ErfolgsZeichen, TropfenLader, fehlerText } from '../components/ui';

const REDIRECT_MS = 6000;

const dauer = (min: number) => `${Math.floor(min / 60)}h ${min % 60}m`;

/**
 * Tagesstand als Ring, der sich bis Ist/Soll füllt. Das Soll steckt in der Antwort nur indirekt:
 * Saldo = Netto − Soll. Ohne Soll (freier Tag) gibt es keinen Ring.
 */
const TagesRing: React.FC<{ nettoMinuten: number; sollMinuten: number; text: string }> = ({ nettoMinuten, sollMinuten, text }) => {
  const anteil = Math.min(100, Math.round((nettoMinuten / sollMinuten) * 100));
  return (
    <div className="relative mx-auto h-36 w-36">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" aria-hidden>
        <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="9" />
        <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="9" strokeLinecap="round"
          pathLength={100} strokeDasharray="100" strokeDashoffset={100 - anteil} className="ui-ring" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold leading-tight">{text}</span>
        <span className="text-sm text-white/75">von {dauer(sollMinuten)}</span>
      </div>
    </div>
  );
};

/**
 * Stempelseite nach dem Scannen des QR-Codes (Deeplink -> /stempeln/:token).
 * Zeigt zuerst den aktuellen Status und stempelt erst nach Bestätigung –
 * ein versehentlich (erneut) geöffneter Link bucht so nichts.
 */
const Stempeln: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const [status, setStatus] = useState<StempelStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<StempelResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Ungültiger Stempel-Link. Bitte den QR-Code am Terminal scannen.');
      return;
    }
    zeiterfassungService
      .status()
      .then(setStatus)
      .catch(() => setError('Status konnte nicht geladen werden. Bitte erneut versuchen.'));
  }, [token]);

  useEffect(() => {
    if (!result && !error) return;
    const t = setTimeout(() => navigate('/arbeitszeiten'), REDIRECT_MS);
    return () => clearTimeout(t);
  }, [result, error, navigate]);

  const stempeln = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      setResult(await zeiterfassungService.stempeln(token));
    } catch (e: any) {
      setError(fehlerText(e, 'Stempel konnte nicht gebucht werden. Bitte erneut versuchen.'));
    } finally {
      setBusy(false);
    }
  };

  const istKommen = result?.aktion === 'KOMMEN';
  const sollMinuten = result?.tagNettoMinuten != null && result.saldoMinuten != null
    ? result.tagNettoMinuten - result.saldoMinuten
    : 0;
  const bg = error
    ? 'bg-red-600'
    : result?.doppelt
    ? 'bg-amber-600'
    : result
    ? istKommen ? 'bg-green-600' : 'bg-blue-700'
    : 'bg-brand-700';

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center text-white px-6 ${bg} transition-colors`}>
      {!result && !error && !status && (
        <>
          <TropfenLader className="h-14 w-14 mb-6" />
          <p className="text-xl">Einen Moment, {user?.firstName}…</p>
        </>
      )}

      {!result && !error && status && (
        <div className="ui-pop text-center max-w-md w-full">
          <div className="flex justify-center mb-4">{status.eingestempelt ? <Icon.Check className="h-20 w-20" /> : <Icon.Uhr className="h-20 w-20" />}</div>
          <p className="text-2xl mb-1">Hallo {user?.firstName}</p>
          <p className="text-lg text-white/80 mb-8">
            {status.eingestempelt
              ? <>Sie sind seit <b>{status.seit} Uhr</b> eingestempelt.</>
              : status.letzterStempel
              ? <>Zuletzt um <b>{status.letzterStempel} Uhr</b> ausgestempelt.</>
              : 'Heute noch nicht eingestempelt.'}
          </p>
          <button
            onClick={stempeln}
            disabled={busy}
            className={`w-full py-5 rounded-2xl text-2xl font-bold shadow-lg transition-colors disabled:opacity-60 ${
              status.eingestempelt ? 'bg-blue-500 hover:bg-blue-400' : 'bg-green-500 hover:bg-green-400'
            }`}
          >
            {busy ? 'Wird gebucht…' : status.eingestempelt ? 'Jetzt ausstempeln' : 'Jetzt einstempeln'}
          </button>
          <button
            onClick={() => navigate('/arbeitszeiten')}
            className="mt-6 px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 transition-colors font-medium"
          >
            Abbrechen
          </button>
        </div>
      )}

      {error && (
        <div className="ui-pop flex flex-col items-center">
          <Icon.Warnung className="h-16 w-16 mb-4" />
          <h1 className="text-3xl font-bold mb-2">Nicht gebucht</h1>
          <p className="text-lg text-white/90 text-center">{error}</p>
        </div>
      )}

      {result && (
        <div className="ui-pop text-center max-w-md w-full">
          <div className="flex justify-center mb-4">
            {result.doppelt ? <Icon.Info className="h-20 w-20" /> : <ErfolgsZeichen art={istKommen ? 'kommen' : 'haken'} className="h-20 w-20" />}
          </div>
          <h1 className="text-4xl font-bold mb-2">
            {result.doppelt
              ? istKommen ? 'Bereits eingestempelt' : 'Bereits ausgestempelt'
              : istKommen ? 'Eingestempelt' : 'Ausgestempelt'}
          </h1>
          <p className="text-2xl mb-1">{result.mitarbeiterName}</p>
          <p className="text-lg text-white/80 mb-6">um {result.zeit} Uhr</p>
          {result.doppelt && (
            <p className="text-sm text-white/80 mb-6">Der erneute Scan wurde nicht gebucht.</p>
          )}

          {!istKommen && result.tagStundenText && (
            <div className="bg-white/15 rounded-2xl p-5 space-y-2">
              {sollMinuten > 0 ? (
                <>
                  <p className="text-sm text-white/80">Heute gearbeitet</p>
                  <TagesRing nettoMinuten={result.tagNettoMinuten ?? 0} sollMinuten={sollMinuten} text={result.tagStundenText} />
                </>
              ) : (
                <p className="text-lg">
                  Heute gearbeitet: <span className="font-bold">{result.tagStundenText}</span>
                </p>
              )}
              {result.saldoText && (
                <p className="text-lg flex items-center justify-center gap-2">
                  {result.saldoMinuten != null && result.saldoMinuten > 0 && <span aria-hidden className="inline-block h-3 w-3 rounded-full bg-green-400 ring-2 ring-white/70" />}
                  {result.saldoMinuten != null && result.saldoMinuten < 0 && <span aria-hidden className="inline-block h-3 w-3 rounded-full bg-red-500 ring-2 ring-white/70" />}
                  {result.saldoText}
                </p>
              )}
            </div>
          )}

          <button
            onClick={() => navigate('/arbeitszeiten')}
            className="mt-8 px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 transition-colors font-medium"
          >
            Zu meinen Arbeitszeiten
          </button>
          <div className="mx-auto mt-5 h-1 w-40 overflow-hidden rounded-full bg-white/20" aria-hidden>
            <div className="ui-ablauf h-full bg-white/70" style={{ animationDuration: `${REDIRECT_MS}ms` }} />
          </div>
          <p className="text-xs text-white/60 mt-2">Weiterleitung in wenigen Sekunden…</p>
        </div>
      )}
    </div>
  );
};

export default Stempeln;
